import type {
  DensitySnapshot,
  InstrumentRef,
  InstrumentStatusView,
  WrappedCoin,
} from "@screener/protocol";
import { Table, TableBody } from "@screener/uikit/components/table";
import { Fragment, useMemo } from "react";
import { DensityRowItem, rowAnnotations } from "./density-row-item";
import { DensityTableHeader } from "./density-table-header";
import type { DensitySort } from "./derive";
import type { RowPinMatch } from "./pin-match";
import type { SituationRow as SituationRowData } from "./situation-derive";
import { SituationRowItem } from "./situation-row-item";
import { useRowNavigation } from "./use-row-navigation";

const EMPTY_PENDING_PIN_IDS: ReadonlySet<string> = new Set();
const EMPTY_PENDING_FAVORITE_BASES: ReadonlySet<string> = new Set();

interface SituationListProps {
  rows: SituationRowData[];
  /** Ситуаций было до фильтров — чтобы отличить «не пропустили» от «нет данных». */
  totalSituations: number;
  /**
   * Ситуации, отпавшие без единого известного участника (все ушли в
   * терминал раньше, чем обновился состав §7.6): не пустое состояние отбора,
   * а отставание состава ситуаций от состава живых плотностей на такт.
   */
  unknownCount: number;
  /** Текущая сортировка — шапка показывает её стрелкой, меняется она на панели фильтров. */
  sort: DensitySort;
  instruments: InstrumentStatusView[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Индекс баз в написании биржи (`StreamState.exchange_bases`) — для тикера строки группы. */
  exchangeBases: ReadonlyMap<string, string>;
  /** Приветствие ещё не пришло: состава ситуаций нет вовсе, а не он пуст. */
  awaitingHello: boolean;
  /**
   * «Только избранные» включён, а список избранного ещё не прочитан или
   * недоступен — та же причина пустоты, что и у `DensityTable`, вставленная
   * рядом с «рассогласованием», а не вместо него (спека `web-situation-list`).
   */
  favoritesUnavailable: boolean;
  /** Открыть детальную панель §9 по строке участника. */
  onOpenRow: (row: DensitySnapshot) => void;
  /** Монеты с заметками §6.4 — то же сопоставление клиента, что и у `DensityTable`. */
  notedBases: Set<string> | null;
  /** Закрепления §6.7, сопоставленные со строками — то же, что и у `DensityTable`. */
  pinMatches: Map<string, RowPinMatch> | null;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: (row: DensitySnapshot) => void;
  /** Закрепить уровень строки участника или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Строки участников, чей собственный запрос закрепления ещё не завершился. */
  pendingPinIds?: ReadonlySet<string>;
  /** Монеты, отмеченные избранным целиком §6.6 — то же, что и у `DensityTable`. */
  favoriteBases: Set<string> | null;
  /** Поставить или снять избранное монеты строки участника целиком. */
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Монеты участников, чьё собственное действие избранного ещё не завершилось. */
  pendingFavoriteBases?: ReadonlySet<string>;
  /**
   * Участники раскрытых групп — состояние вкладки, живёт в экране
   * (design.md, решение 4). Составом, а не ключом ситуации: ключ §5 не
   * уникален (см. `SituationRow.key`), и раскрытие по нему раскрывало разом
   * все одноимённые группы. Группа считается раскрытой, если хоть один её
   * участник здесь: так раскрытие переживает и приход нового участника, и
   * уход прежнего — то самое «сохраняется, пока ситуация остаётся в списке»
   * (web-situation-list).
   */
  expandedMembers: ReadonlySet<string>;
  /** Раскрыть группу или свернуть уже раскрытую. */
  onToggleExpand: (row: SituationRowData) => void;
  /** Скрыть монету целиком — хоткей `h` строки (§75 п. 2). */
  onHideCoin: (base: string) => void;
  /** Скопировать тикер строки участника в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: (row: DensitySnapshot) => void;
}

function instrumentKey(instrument: InstrumentRef): string {
  return `${instrument.exchange}:${instrument.market}:${instrument.base}${instrument.quote}`;
}

/**
 * Список ситуаций §5 — второе представление того же живого состава §9. Своя
 * таблица с той же общей шапкой сортировки, что у `DensityTable`
 * (`DensityTableHeader`, design.md change stage6-coin-view, решение 2): тело
 * чередует строку группы во всю ширину и строки участников — те же
 * `DensityRow`, без изменений.
 */
export function SituationList({
  rows,
  totalSituations,
  unknownCount,
  sort,
  instruments,
  wrapped,
  exchangeBases,
  awaitingHello,
  favoritesUnavailable,
  onOpenRow,
  notedBases,
  pinMatches,
  onOpenNotes,
  onTogglePin,
  pendingPinIds = EMPTY_PENDING_PIN_IDS,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases = EMPTY_PENDING_FAVORITE_BASES,
  expandedMembers,
  onToggleExpand,
  onHideCoin,
  onCopyTicker,
}: SituationListProps) {
  const isExpanded = (row: SituationRowData) => row.memberIds.some((id) => expandedMembers.has(id));

  // Единый упорядоченный список ключей видимых строк — ключ группы, затем
  // `density_id` её участников, если группа раскрыта (design.md, решение 3).
  const navKeys = useMemo(() => {
    const keys: string[] = [];
    for (const row of rows) {
      keys.push(row.key);
      if (row.memberIds.some((id) => expandedMembers.has(id))) {
        for (const member of row.members) {
          keys.push(member.density_id);
        }
      }
    }
    return keys;
  }, [rows, expandedMembers]);
  const { focusable, onFocus, onKeyDown: onRowKeyDown } = useRowNavigation(navKeys);

  // Состав устаревших инструментов меняется со статусом, а не с каждым кадром.
  const staleInstruments = useMemo(
    () =>
      new Set(
        instruments
          .filter((entry) => entry.status !== "live")
          .map((entry) => instrumentKey(entry.instrument)),
      ),
    [instruments],
  );
  const annotationSources = {
    notedBases,
    pinMatches,
    pendingPinIds,
    favoriteBases,
    pendingFavoriteBases,
  };

  // Все ситуации, которые вообще были, отпали без единого известного
  // участника (§7.6 «Группа пережила всех своих участников»): отбор тут ни
  // при чём — терминальное событие убрало плотность раньше, чем обновился
  // состав ситуаций. Мешанины двух причин быть не может: `unknownCount`
  // считает только группы, у которых нет ни одного снимка вовсе, и если их
  // ровно столько же, сколько всех ситуаций, отбор ни одну из них не отсеял.
  const disappearedWithUnknownMembers = rows.length === 0 && unknownCount === totalSituations;

  const emptyMessage = awaitingHello
    ? "Состав ситуаций ещё не получен: приветствие демона не пришло."
    : totalSituations === 0
      ? "Живых ситуаций нет — демон не прислал ни одной."
      : favoritesUnavailable
        ? "Избранное недоступно: список не прочитан."
        : disappearedWithUnknownMembers
          ? "Состав ситуаций разошёлся с живыми плотностями: их участников на экране пока нет."
          : "Фильтры не пропустили ни одной ситуации.";

  return (
    <>
      <Table data-row-list className="text-[13px]">
        <DensityTableHeader showsSort sort={sort} />

        <TableBody>
          {rows.length === 0
            ? null
            : rows.map((row) => {
                const expanded = isExpanded(row);
                return (
                  <Fragment key={row.key}>
                    <SituationRowItem
                      row={row}
                      wrapped={wrapped}
                      exchangeBases={exchangeBases}
                      expanded={expanded}
                      focusable={focusable(row.key)}
                      onFocus={onFocus}
                      onKeyDown={onRowKeyDown}
                      onToggleExpand={onToggleExpand}
                    />
                    {expanded
                      ? row.members.map((member) => (
                          <DensityRowItem
                            key={member.density_id}
                            row={member}
                            wrapped={wrapped}
                            stale={staleInstruments.has(instrumentKey(member.instrument))}
                            focusable={focusable(member.density_id)}
                            {...rowAnnotations(member, annotationSources)}
                            onFocus={onFocus}
                            onKeyDown={onRowKeyDown}
                            onOpenRow={onOpenRow}
                            onOpenNotes={onOpenNotes}
                            onTogglePin={onTogglePin}
                            onToggleFavorite={onToggleFavorite}
                            onHideCoin={onHideCoin}
                            onCopyTicker={onCopyTicker}
                          />
                        ))
                      : null}
                  </Fragment>
                );
              })}
        </TableBody>
      </Table>
      {/* Пустое состояние — вне горизонтально прокручиваемой таблицы: иначе оно
        центрируется по её полной ширине и на узком окне уходит за край. */}
      {rows.length === 0 ? (
        <p className="flex min-h-20 items-center justify-center px-4 py-3 text-center text-[13px] text-balance text-muted-foreground">
          {emptyMessage}
        </p>
      ) : null}
    </>
  );
}
